import type { AssociationSettingsHandle } from '@/components/accounting/association-settings-handle';
import { authService } from '@/services/auth-service';
import { classifyApiError, isAuthRecoverable, type ClassifiedError } from '@/utils/api-error';
import { logger } from '@/utils/logger';

/** A named association rule section (dues/late-fees/statement/basis). */
export interface NamedSection {
	name: string;
	handle: AssociationSettingsHandle | null;
}

export interface SectionFailure {
	section: string;
	error: ClassifiedError;
}

export type ReauthResult<T> = { value: T } | { error: ClassifiedError };

/**
 * Run an async API call with one silent token-refresh retry.
 *
 * Transient 5xx/network failures are already retried (with exponential backoff,
 * 3×) inside apiService.fetchApi — by the time an error reaches here it has been
 * through that. So this layer only adds what the transport can't: on an
 * auth-recoverable failure (401/403) it refreshes tokens once and re-runs `fn`.
 * Every error is logged with the `area` tag so field failures are diagnosable.
 */
export const runWithReauth = async <T>(fn: () => Promise<T>, area: string): Promise<ReauthResult<T>> => {
	const attempt = async (label: string): Promise<ReauthResult<T>> => {
		try {
			return { value: await fn() };
		} catch (e) {
			const error = classifyApiError(e);
			logger.error(`[${area}] ${label}`, { kind: error.kind, status: error.status, message: error.message });
			return { error };
		}
	};

	const first = await attempt('failed');
	if ('value' in first || !isAuthRecoverable(first.error.kind)) {
		return first;
	}

	logger.warn(`[${area}] ${first.error.kind} — attempting silent token refresh`);
	const refreshed = await authService.refreshTokens().catch((re) => {
		logger.error(`[${area}] token refresh threw`, re);
		return null;
	});
	if (!refreshed) {
		logger.error(`[${area}] token refresh unsuccessful — surfacing ${first.error.kind}`);
		return first;
	}

	const second = await attempt('failed after token refresh');
	if ('value' in second) {
		logger.warn(`[${area}] recovered after token refresh`);
	}
	return second;
};

/**
 * Flush each rule section against the association id. Sections self-skip when
 * their draft is empty (see association-settings-handle). Each gets one
 * silent-reauth retry; the sections that still fail are returned so the caller
 * can present a single partial-save failure with retry/report.
 */
export const flushRuleSections = async (
	sections: NamedSection[],
	associationId: string,
	area: string,
): Promise<SectionFailure[]> => {
	const failures: SectionFailure[] = [];
	// Sequential (not Promise.all): each section may trigger a token refresh, and
	// concurrent refreshTokens() calls race on credential storage.
	for (const { name, handle } of sections) {
		if (!handle) continue;
		const result = await runWithReauth(() => handle.save(associationId), `${area}:${name}`);
		if ('error' in result) {
			failures.push({ section: name, error: result.error });
		}
	}
	return failures;
};
